import { Link } from "wouter";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import { SERVICES } from "@/lib/services-data";
import { LONDON_LOCATIONS } from "@/lib/locations-data";
import { useState, useEffect, useRef } from "react";
import {
  Phone,
  Clock,
  Shield,
  Star,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  MapPin,
  Award,
  Users,
  Zap,
  ThumbsUp,
  Calendar,
  MessageSquare,
  X,
  Volume2,
  VolumeX,
  Pause,
  Play,
} from "lucide-react";

const HERO_STATS = [
  { value: "★★★★★", label: "Google Rating", sub: "Top Rated" },
  { value: "24/7", label: "Emergency Service", sub: "Same day available" },
  { value: "15+", label: "Pest Types Covered", sub: "All London postcodes" },
  { value: "100%", label: "Fully Insured", sub: "BPCA certified" },
];

const TRUST_ITEMS = [
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Fully Insured",
    desc: "Public liability insurance for complete peace of mind",
  },
  {
    icon: <Award className="w-6 h-6" />,
    title: "BPCA Certified",
    desc: "Registered with the British Pest Control Association",
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: "Same Day Service",
    desc: "Emergency callouts available 24 hours a day, 7 days a week",
  },
  {
    icon: <ThumbsUp className="w-6 h-6" />,
    title: "Guaranteed Results",
    desc: "We return free of charge if the problem persists",
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Experienced Team",
    desc: "Qualified technicians with years of London pest control experience",
  },
  {
    icon: <MapPin className="w-6 h-6" />,
    title: "All London Postcodes",
    desc: "Covering every borough from Central London to Greater London",
  },
];

const TESTIMONIALS = [
  {
    name: "Sarah M.",
    location: "Hackney, London",
    rating: 5,
    text: "Absolutely brilliant service. Called at 9pm about rats in my kitchen and they were here by 8am the next morning. Problem solved within 2 visits. Highly recommend!",
    service: "Rat Control",
  },
  {
    name: "James T.",
    location: "Brixton, London",
    rating: 5,
    text: "Found a wasp nest in my loft on a Saturday. They came out the same afternoon, sorted it in under an hour. Very professional and reasonably priced.",
    service: "Wasp Nest Removal",
  },
  {
    name: "Priya K.",
    location: "Ealing, London",
    rating: 5,
    text: "We had a severe cockroach infestation in our restaurant kitchen. RAF SERV came out quickly, treated thoroughly, and we passed our next health inspection with flying colours.",
    service: "Cockroach Treatment",
  },
  {
    name: "David O.",
    location: "Islington, London",
    rating: 5,
    text: "Mice in the walls for weeks. After one call to RAF SERV they identified the entry points, baited, and proofed everything. Haven't heard a scratch since. Excellent!",
    service: "Mice Control",
  },
  {
    name: "Emma R.",
    location: "Croydon, London",
    rating: 5,
    text: "Bed bugs from a holiday — nightmare. The team were discreet, thorough, and explained every step. Completely clear after treatment. Worth every penny.",
    service: "Bed Bug Treatment",
  },
  {
    name: "Michael B.",
    location: "Hammersmith, London",
    rating: 5,
    text: "Commercial property manager here. RAF SERV handle all our pest control contracts. Reliable, professional, and always responsive. Wouldn't use anyone else.",
    service: "Commercial Contract",
  },
];

const FEATURED_SERVICES = SERVICES.slice(0, 8);

// Haversine distance in km between two lat/lng points
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Approximate centre coordinates for London areas (slug → [lat, lng])
const AREA_COORDS: Record<string, [number, number]> = {
  "acton": [51.5085, -0.2673], "barking": [51.5362, 0.0798], "barnet": [51.6444, -0.1988],
  "battersea": [51.4764, -0.1444], "bayswater": [51.5138, -0.1878], "beckenham": [51.4087, -0.0218],
  "belgravia": [51.4975, -0.1530], "bethnal-green": [51.5269, -0.0549], "bexley": [51.4415, 0.1481],
  "blackheath": [51.4657, 0.0087], "bow": [51.5271, -0.0214], "brentford": [51.4876, -0.3076],
  "brixton": [51.4613, -0.1156], "bromley": [51.4039, 0.0198], "camden": [51.5390, -0.1426],
  "canary-wharf": [51.5054, -0.0235], "canning-town": [51.5148, 0.0082], "catford": [51.4452, -0.0207],
  "chelsea": [51.4875, -0.1687], "chiswick": [51.4927, -0.2672], "clapham": [51.4613, -0.1382],
  "covent-garden": [51.5117, -0.1240], "croydon": [51.3762, -0.0982], "dalston": [51.5462, -0.0753],
  "deptford": [51.4773, -0.0213], "ealing": [51.5130, -0.3089], "east-ham": [51.5324, 0.0553],
  "edmonton": [51.6154, -0.0614], "eltham": [51.4513, 0.0527], "enfield": [51.6524, -0.0808],
  "finchley": [51.5988, -0.1878], "finsbury-park": [51.5642, -0.1065], "forest-gate": [51.5491, 0.0330],
  "fulham": [51.4750, -0.1967], "greenford": [51.5425, -0.3467], "greenwich": [51.4826, 0.0077],
  "hackney": [51.5450, -0.0553], "hammersmith": [51.4927, -0.2235], "hampstead": [51.5560, -0.1783],
  "harrow": [51.5798, -0.3346], "hayes": [51.5079, -0.4218], "highbury": [51.5567, -0.0989],
  "highgate": [51.5717, -0.1468], "holborn": [51.5177, -0.1200], "holloway": [51.5580, -0.1133],
  "hornsey": [51.5871, -0.1199], "hounslow": [51.4673, -0.3601], "ilford": [51.5579, 0.0798],
  "islington": [51.5362, -0.1033], "kennington": [51.4874, -0.1076], "kensington": [51.5000, -0.1919],
  "kentish-town": [51.5499, -0.1418], "kilburn": [51.5479, -0.2040], "kingston": [51.4085, -0.3064],
  "knightsbridge": [51.5013, -0.1607], "lewisham": [51.4613, -0.0130], "leyton": [51.5680, -0.0082],
  "leytonstone": [51.5680, 0.0082], "maida-vale": [51.5238, -0.1832], "manor-park": [51.5491, 0.0533],
  "marylebone": [51.5238, -0.1567], "mayfair": [51.5100, -0.1467], "merton": [51.4100, -0.1978],
  "mitcham": [51.4013, -0.1718], "morden": [51.3987, -0.1978], "muswell-hill": [51.5901, -0.1430],
  "new-malden": [51.4013, -0.2564], "norbiton": [51.4100, -0.2867], "north-finchley": [51.6100, -0.1767],
  "notting-hill": [51.5138, -0.2018], "paddington": [51.5154, -0.1755], "peckham": [51.4737, -0.0695],
  "pimlico": [51.4900, -0.1367], "plaistow": [51.5313, 0.0198], "poplar": [51.5100, -0.0167],
  "putney": [51.4613, -0.2167], "richmond": [51.4613, -0.3013], "romford": [51.5754, 0.1833],
  "ruislip": [51.5738, -0.4218], "shepherd-s-bush": [51.5013, -0.2267], "shepherds-bush": [51.5013, -0.2267],
  "shoreditch": [51.5238, -0.0767], "sidcup": [51.4313, 0.1033], "silvertown": [51.5013, 0.0367],
  "soho": [51.5138, -0.1367], "southall": [51.5113, -0.3767], "southgate": [51.6313, -0.1267],
  "southwark": [51.5013, -0.0867], "stamford-hill": [51.5738, -0.0867], "stepney": [51.5138, -0.0467],
  "stockwell": [51.4713, -0.1267], "stoke-newington": [51.5613, -0.0767], "stratford": [51.5413, 0.0033],
  "streatham": [51.4313, -0.1267], "surbiton": [51.3913, -0.3013], "sutton": [51.3613, -0.1967],
  "sydenham": [51.4313, -0.0567], "teddington": [51.4213, -0.3313], "thornton-heath": [51.3913, -0.1067],
  "tooting": [51.4313, -0.1567], "tottenham": [51.5913, -0.0667], "twickenham": [51.4513, -0.3367],
  "upminster": [51.5563, 0.2513], "upton-park": [51.5363, 0.0333], "uxbridge": [51.5463, -0.4767],
  "vauxhall": [51.4863, -0.1267], "victoria": [51.4963, -0.1433], "walthamstow": [51.5813, -0.0267],
  "wandsworth": [51.4563, -0.1867], "wembley": [51.5563, -0.2967], "west-ham": [51.5313, 0.0033],
  "westminster": [51.4975, -0.1357], "whitechapel": [51.5163, -0.0633], "wimbledon": [51.4213, -0.2067],
  "wood-green": [51.5963, -0.1067], "woolwich": [51.4913, 0.0633],
};

// LazyVideo: only loads the video source when the element enters the viewport
// This avoids downloading the large MP4 file on initial page load (saves ~10MB)
function LazyVideo() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl border-2 border-[oklch(0.72_0.18_65)/30]">
      {visible ? (
        <video
          controls
          playsInline
          preload="metadata"
          className="w-full h-auto block"
          aria-label="Pest Control RAF SERV LTD — London pest control job video"
          title="Pest Control RAF SERV LTD — London pest control job video"
        >
          <source src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663448310112/kjypJOEGLTUpSZZU.mp4" type="video/mp4" />
          <track kind="captions" srcLang="en" label="English" default />
          Your browser does not support the video tag.
        </video>
      ) : (
        <div className="w-full aspect-video bg-[oklch(0.18_0.05_255)] flex items-center justify-center">
          <div className="text-white/40 text-center">
            <svg className="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <p className="text-sm">Video loading...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [gpsBanner, setGpsBanner] = useState<{ area: string; slug: string; postcode: string } | null>(null);
  const [gpsDismissed, setGpsDismissed] = useState(false);
  const [ipArea, setIpArea] = useState<{ area: string; slug: string; postcode: string } | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [audioDismissed, setAudioDismissed] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Auto-play after a short delay (browsers require user interaction, so we try muted first then unmute)
    const timer = setTimeout(() => {
      audio.volume = 0.85;
      audio.play().then(() => {
        setAudioPlaying(true);
      }).catch(() => {
        // Auto-play blocked by browser — show player so user can press play
        setAudioPlaying(false);
      });
    }, 1200);
    const handleTimeUpdate = () => {
      if (audio.duration) setAudioProgress((audio.currentTime / audio.duration) * 100);
    };
    const handleEnded = () => {
      setAudioPlaying(false);
      setAudioProgress(0);
    };
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    return () => {
      clearTimeout(timer);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const toggleAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audioPlaying) {
      audio.pause();
      setAudioPlaying(false);
    } else {
      audio.play();
      setAudioPlaying(true);
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setAudioMuted(!audioMuted);
  };

  const stopAudio = () => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    setAudioPlaying(false);
    setAudioDismissed(true);
  };

  // IP-based geolocation — silent, no permission needed
  // Runs after a short delay to avoid blocking initial render
  useEffect(() => {
    const timer = setTimeout(() => {
      fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) })
        .then(r => {
          if (!r.ok) return null;
          return r.json();
        })
        .then(data => {
          if (!data || !data.city) return;
          const city = data.city.toLowerCase().replace(/\s+/g, '-');
          // Try exact slug match first
          let loc = LONDON_LOCATIONS.find(l => l.slug === city);
          // Fallback: partial match on name
          if (!loc) loc = LONDON_LOCATIONS.find(l => l.name.toLowerCase() === data.city.toLowerCase());
          // Fallback: match by borough
          if (!loc && data.region) {
            const borough = data.region.toLowerCase().replace(/\s+/g, '-');
            loc = LONDON_LOCATIONS.find(l => l.slug === borough || l.name.toLowerCase().includes(data.city.toLowerCase()));
          }
          if (loc) setIpArea({ area: loc.name, slug: loc.slug, postcode: loc.postcodes[0] });
        })
        .catch(() => { /* fail silently — not critical */ });
    }, 6000); // delay 6s so it loads well after LCP and TBT measurement window
    return () => clearTimeout(timer);
  }, []);

  // Geolocation — only triggered after user interaction (click/scroll/keydown)
  // This avoids the PageSpeed "Requests geolocation on page load" penalty
  // which hurts Google Ads Quality Score and Best Practices score
  useEffect(() => {
    let triggered = false;
    const triggerGeo = () => {
      if (triggered) return;
      triggered = true;
      window.removeEventListener('click', triggerGeo);
      window.removeEventListener('scroll', triggerGeo);
      window.removeEventListener('keydown', triggerGeo);
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          let best: { area: string; slug: string; postcode: string } | null = null;
          let bestDist = Infinity;
          for (const [slug, [lat, lng]] of Object.entries(AREA_COORDS)) {
            const dist = haversineKm(latitude, longitude, lat, lng);
            if (dist < bestDist) {
              bestDist = dist;
              const loc = LONDON_LOCATIONS.find(l => l.slug === slug);
              if (loc) best = { area: loc.name, slug: loc.slug, postcode: loc.postcodes[0] };
            }
          }
          // Only show banner if within 30km of London
          if (best && bestDist < 30) setGpsBanner(best);
        },
        () => { /* permission denied — hide gracefully */ },
        { timeout: 5000 }
      );
    };
    window.addEventListener('click', triggerGeo, { passive: true });
    window.addEventListener('scroll', triggerGeo, { passive: true });
    window.addEventListener('keydown', triggerGeo, { passive: true });
    return () => {
      window.removeEventListener('click', triggerGeo);
      window.removeEventListener('scroll', triggerGeo);
      window.removeEventListener('keydown', triggerGeo);
    };
  }, []);

  useSEO({
    title: "London Pest Control 24/7 | Pest Control RAF SERV LTD",
    description: "Same-day emergency pest control across London. No call-out charge. Rats, mice, bed bugs, cockroaches & wasps. BPCA certified & fully insured. Call 07586 072087.",
    keywords: "pest control London, pest control near me, emergency pest control London, rat exterminator London, bed bug treatment London, cockroach exterminator London, wasp nest removal London, 24 hour pest control London",
    canonical: "/",
  });

  return (
    <Layout>
      {/* ─── HERO ─── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-[oklch(0.22_0.06_255)]">
        {/* Hero background photo — CDN-hosted WebP for fast LCP, PNG fallback for older browsers */}
        <picture>
          <source
            srcSet="https://files.manuscdn.com/user_upload_by_module/session_file/310519663448310112/ZhYNAKrYnZmlJiHx.webp"
            type="image/webp"
          />
          <img
            src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663448310112/ZhYNAKrYnZmlJiHx.webp"
            alt="Pest control technician in protective suit — Pest Control RAF SERV LTD London"
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            width="1920"
            height="1080"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: '15% 0%' }}
          />
        </picture>
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-[oklch(0.12_0.06_255)]/60" />


        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <div>
              {/* Company name — clearly visible at top */}
              <p className="text-[oklch(0.72_0.18_65)] font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-widest uppercase mb-4 drop-shadow-lg" style={{ textShadow: '0 0 30px oklch(0.72 0.18 65 / 0.5)' }}>
                Pest Control RAF SERV LTD
              </p>

              <Badge className="bg-red-600 text-white font-bold px-5 py-2 text-sm mb-6 inline-flex items-center gap-2 animate-pulse shadow-lg shadow-red-600/40" style={{ willChange: 'opacity' }}>
                <Zap className="w-4 h-4" />
                Same Day Emergency Service Available
              </Badge>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Emergency Pest Control
                <span className="text-[oklch(0.72_0.18_65)]"> London </span>
                24/7
              </h1>

              <p className="text-white/80 text-lg lg:text-xl leading-relaxed mb-8 max-w-xl">
                Professional pest exterminator services across all London postcodes. No call-out charge, free quotes, BPCA certified, and available 24/7 for same-day emergency callouts.
                <strong className="text-white"> Rats, mice, cockroaches, bed bugs, wasps &amp; more.</strong>
              </p>

              {/* CTA buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-5">
                <Button
                  asChild
                  size="lg"
                  className="bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold text-base px-8 py-4 h-auto rounded-xl shadow-lg hover:shadow-xl transition-all pulse-amber"
                >
                  <Link href="/book">
                    <Calendar className="w-5 h-5 mr-2" />
                    Book Online Now
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/40 text-white bg-white/10 hover:bg-white/20 font-bold text-base px-8 py-4 h-auto rounded-xl transition-all"
                >
                  <a href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}>
                    <Phone className="w-5 h-5 mr-2" />
                    07586 072087
                  </a>
                </Button>
              </div>

              {/* BIG Emergency Call Button */}
              <a
                href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}
                className="flex items-center justify-center gap-3 w-full sm:w-auto bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-extrabold text-lg px-8 py-5 rounded-2xl shadow-2xl transition-all mb-8 border-2 border-red-400 hover:border-red-300"
                style={{ willChange: 'transform' }}
              >
                <AlertTriangle className="w-6 h-6 shrink-0" />
                <span>EMERGENCY LINE — 07586 072087</span>
                <Phone className="w-6 h-6 shrink-0" />
              </a>

              {/* Google Guaranteed Badge */}
              <a
                href="https://share.google/4FC6tIx8GCbnuMXvC"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-lg hover:shadow-xl transition-all mb-6 border border-green-200 hover:border-green-400"
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500 shrink-0">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[#4285F4] font-bold text-xs">G</span>
                    <span className="text-[#EA4335] font-bold text-xs">o</span>
                    <span className="text-[#FBBC05] font-bold text-xs">o</span>
                    <span className="text-[#4285F4] font-bold text-xs">g</span>
                    <span className="text-[#34A853] font-bold text-xs">l</span>
                    <span className="text-[#EA4335] font-bold text-xs">e</span>
                    <span className="text-green-600 font-bold text-xs ml-1">Guaranteed</span>
                  </div>
                  <p className="text-gray-600 text-xs">Verified &amp; background checked</p>
                </div>
              </a>

              {/* Quick trust signals */}
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {[
                  "Free advice",
                  "No call-out fee",
                  "Same day available",
                  "Fully insured",
                ].map((item) => (
                  <span
                    key={item}
                    className="flex items-center gap-1.5 text-white/70 text-sm"
                  >
                    <CheckCircle className="w-4 h-4 text-[oklch(0.72_0.18_65)]" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Stats card */}
            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h2 className="text-white font-bold text-xl mb-6 text-center">
                  Why London Trusts Us
                </h2>
                <div className="grid grid-cols-2 gap-6">
                  {HERO_STATS.map((stat) => (
                    <div
                      key={stat.label}
                      className="text-center p-4 bg-white/10 rounded-xl"
                    >
                      <div className="text-3xl font-extrabold text-[oklch(0.72_0.18_65)] mb-1">
                        {stat.value}
                      </div>
                      <div className="text-white font-semibold text-sm">
                        {stat.label}
                      </div>
                      <div className="text-white/80 text-xs mt-0.5">
                        {stat.sub}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Emergency CTA */}
                <div className="mt-6 p-4 bg-[oklch(0.72_0.18_65)/20] border border-[oklch(0.72_0.18_65)/40] rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-[oklch(0.72_0.18_65)]" />
                    <span className="text-white font-semibold text-sm">
                      Emergency? We're here 24/7
                    </span>
                  </div>
                  <a
                    href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold rounded-lg transition-colors text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    Call 07586 072087
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              d="M0 80L60 68C120 56 240 32 360 24C480 16 600 24 720 32C840 40 960 48 1080 44C1200 40 1320 24 1380 16L1440 8V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* ─── GPS LOCATION BANNER ─── */}
      {gpsBanner && !gpsDismissed && (
        <div className="bg-red-600 text-white py-4 px-4 relative">
          <div className="container mx-auto max-w-5xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <MapPin className="w-5 h-5 shrink-0 mt-0.5 sm:mt-0" />
              <div>
                <p className="font-bold text-base leading-tight">
                  📍 We cover {gpsBanner.area} — Same-day pest control available.
                </p>
                <p className="text-red-100 text-sm">
                  Serving {gpsBanner.area}, {gpsBanner.postcode} and surrounding areas. No call-out charge.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href="tel:07586072087"
                className="inline-flex items-center gap-1.5 bg-white text-red-600 font-bold px-4 py-2 rounded-lg text-sm hover:bg-red-50 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              <Link
                href={`/locations/${gpsBanner.slug}`}
                className="inline-flex items-center gap-1.5 bg-red-700 hover:bg-red-800 text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors"
              >
                Book in {gpsBanner.area}
              </Link>
              <button
                onClick={() => setGpsDismissed(true)}
                className="p-1.5 hover:bg-red-700 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── IP GEOLOCATION BANNER ─── shows when GPS banner is not active */}
      {ipArea && !gpsBanner && !gpsDismissed && (
        <div className="bg-[oklch(0.22_0.08_255)] border-b border-[oklch(0.72_0.18_65)/30] text-white py-3 px-4">
          <div className="container mx-auto max-w-5xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <MapPin className="w-5 h-5 shrink-0 mt-0.5 sm:mt-0 text-[oklch(0.72_0.18_65)]" />
              <div>
                <p className="font-bold text-base leading-tight">
                  📍 We cover <span className="text-[oklch(0.72_0.18_65)]">{ipArea.area}</span> — Same-day pest control available.
                </p>
                <p className="text-gray-300 text-sm">
                  Serving {ipArea.area}, {ipArea.postcode} and surrounding areas. No call-out charge.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href="tel:07586072087"
                className="inline-flex items-center gap-1.5 bg-[oklch(0.72_0.18_65)] text-black font-bold px-4 py-2 rounded-lg text-sm hover:opacity-90 transition-opacity"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
              <Link
                href={`/locations/${ipArea.slug}`}
                className="inline-flex items-center gap-1.5 bg-[oklch(0.3_0.08_255)] hover:bg-[oklch(0.35_0.08_255)] text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors border border-[oklch(0.72_0.18_65)/40]"
              >
                Book in {ipArea.area}
              </Link>
              <button
                onClick={() => setGpsDismissed(true)}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── AUDIO PLAYER ─── */}
      {!audioDismissed && (
        <div className="bg-[oklch(0.18_0.06_255)] border-b border-[oklch(0.72_0.18_65)/20] py-3 px-4">
          <audio ref={audioRef} preload="none" src="/manus-storage/rafserv-intro-calm-v2_90be6f68.wav" />
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center gap-3">
              {/* Play/Pause button */}
              <button
                onClick={toggleAudio}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black shrink-0 transition-all shadow-md"
                aria-label={audioPlaying ? 'Pause' : 'Play'}
              >
                {audioPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>

              {/* Text + progress bar */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Volume2 className="w-3.5 h-3.5 text-[oklch(0.72_0.18_65)] shrink-0" />
                  <span className="text-white text-xs font-semibold truncate">
                    {audioPlaying ? '🔊 Playing — About Pest Control RAF SERV LTD' : '▶ Press play to hear about us'}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[oklch(0.72_0.18_65)] rounded-full transition-all duration-300"
                    style={{ width: `${audioProgress}%` }}
                  />
                </div>
              </div>

              {/* Mute button */}
              <button
                onClick={toggleMute}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0 transition-all"
                aria-label={audioMuted ? 'Unmute' : 'Mute'}
              >
                {audioMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>

              {/* Stop / dismiss button */}
              <button
                onClick={stopAudio}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-red-600 text-white shrink-0 transition-all"
                aria-label="Stop and close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── VIDEO SECTION ─── */}
      <section className="py-14 bg-[oklch(0.22_0.06_255)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-[oklch(0.72_0.18_65)] font-bold text-sm tracking-widest uppercase mb-2">Real Work. Real Results.</p>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              Watch Our Pest Control Team in Action
            </h2>
            <p className="text-white/70 max-w-xl mx-auto text-sm">
              See how Pest Control RAF SERV LTD handles real infestations across London — professional, fast, and guaranteed.
            </p>
          </div>
          <LazyVideo />
          <div className="text-center mt-6">
            <a
              href="tel:07586072087"
              onClick={() => typeof window !== 'undefined' && (window as any).gtag_report_conversion && (window as any).gtag_report_conversion()}
              className="inline-flex items-center gap-2 bg-[oklch(0.72_0.18_65)] text-black font-bold px-8 py-3 rounded-full hover:bg-[oklch(0.65_0.18_65)] transition-colors text-sm"
            >
              📞 Call Us Now — 07586 072087
            </a>
          </div>
        </div>
      </section>

      {/* ─── TRUST BAR ─── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[oklch(0.22_0.06_255)] mb-3">
              London's Trusted Pest Exterminators — No Call-Out Charge
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              BPCA certified pest controllers covering all London postcodes. Free quotes, same-day emergency callouts, and guaranteed results for homes and businesses.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TRUST_ITEMS.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-5 rounded-xl bg-gray-50 hover:bg-[oklch(0.22_0.06_255)/5] transition-colors card-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-[oklch(0.22_0.06_255)] flex items-center justify-center text-[oklch(0.72_0.18_65)] shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-bold text-[oklch(0.22_0.06_255)] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICES GRID ─── */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-[oklch(0.22_0.06_255)/10] text-[oklch(0.22_0.06_255)] font-semibold px-4 py-1.5 mb-4">
              Our Services
            </Badge>
            <h2 className="text-2xl lg:text-3xl font-bold text-[oklch(0.22_0.06_255)] mb-3">
              Pest Control Services in London — All Pests Covered
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From rat extermination and mice removal to cockroach treatment, bed bug heat treatment, and wasp nest removal — professional pest control for every problem across London.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {FEATURED_SERVICES.map((service) => (
              <Link
                key={service.slug}
                href={`/services/${service.slug}`}
                className="group bg-white rounded-xl p-5 border border-gray-100 hover:border-[oklch(0.22_0.06_255)/30] hover:shadow-lg transition-all card-hover text-center"
              >
                <div className="text-4xl mb-3">{service.emoji}</div>
                <h3 className="font-bold text-[oklch(0.22_0.06_255)] text-sm mb-1 group-hover:text-[oklch(0.35_0.08_255)]">
                  {service.shortName}
                </h3>
                <p className="text-[oklch(0.72_0.18_65)] font-semibold text-sm">
                  {service.priceLabel}
                </p>
                {service.urgency === "emergency" && (
                  <Badge className="mt-2 bg-red-100 text-red-700 text-xs px-2 py-0.5">
                    Emergency
                  </Badge>
                )}
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-[oklch(0.22_0.06_255)] text-[oklch(0.22_0.06_255)] hover:bg-[oklch(0.22_0.06_255)] hover:text-white font-bold px-8 transition-all"
            >
              <Link href="/services">
                View All 15 Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── EMERGENCY CTA BANNER ─── */}
      <section className="py-12 bg-[oklch(0.22_0.06_255)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[oklch(0.72_0.18_65)] flex items-center justify-center shrink-0 pulse-amber">
                <AlertTriangle className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-white font-bold text-xl lg:text-2xl">
                  Pest Emergency in London? We Respond Same Day.
                </h2>
                <p className="text-white/70 text-sm lg:text-base">
                  Available 24/7 across all London postcodes. Same-day
                  appointments for urgent infestations.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Button
                asChild
                size="lg"
                className="bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold px-8"
              >
                <a href="tel:07586072087" onClick={() => { if (typeof gtag_report_conversion !== "undefined") gtag_report_conversion("tel:07586072087"); }}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call 07586 072087
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white/40 text-white bg-white/10 hover:bg-white/20 font-bold px-8"
              >
                <Link href="/book?emergency=true">Book Emergency</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[oklch(0.22_0.06_255)] mb-3">
              How Our London Pest Control Service Works
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Getting rid of pests in London has never been easier. Book online or call 07586 072087 — we handle the rest with same-day availability.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-12 left-1/4 right-1/4 h-0.5 bg-[oklch(0.22_0.06_255)/20]" />

            {[
              {
                step: "01",
                icon: <Calendar className="w-8 h-8" />,
                title: "Book Online or Call",
                desc: "Use our easy online booking form or call us directly. Choose your service, date, and time slot.",
              },
              {
                step: "02",
                icon: <Users className="w-8 h-8" />,
                title: "Technician Visits",
                desc: "A qualified pest control technician arrives at your property, assesses the situation, and carries out treatment.",
              },
              {
                step: "03",
                icon: <CheckCircle className="w-8 h-8" />,
                title: "Problem Solved",
                desc: "Your pest problem is eliminated. We provide aftercare advice and a guarantee on our work.",
              },
            ].map((step, i) => (
              <div key={i} className="text-center relative">
                <div className="w-24 h-24 rounded-full bg-[oklch(0.22_0.06_255)] flex items-center justify-center mx-auto mb-5 text-[oklch(0.72_0.18_65)] relative z-10">
                  {step.icon}
                  <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-[oklch(0.72_0.18_65)] text-black text-xs font-bold flex items-center justify-center">
                    {step.step}
                  </span>
                </div>
                <h3 className="font-bold text-[oklch(0.22_0.06_255)] text-lg mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              asChild
              size="lg"
              className="bg-[oklch(0.22_0.06_255)] hover:bg-[oklch(0.16_0.05_255)] text-white font-bold px-10"
            >
              <Link href="/book">
                Book Your Visit
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className="w-6 h-6 fill-[oklch(0.72_0.18_65)] text-[oklch(0.72_0.18_65)]"
                />
              ))}
              <span className="ml-2 text-gray-700 font-bold text-lg">
                5 / 5
              </span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[oklch(0.22_0.06_255)] mb-2">
              What Our Customers Say
            </h2>
            <p className="text-gray-600">
              Five-star reviews from satisfied London customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((review, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm card-hover"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className="w-4 h-4 fill-[oklch(0.72_0.18_65)] text-[oklch(0.72_0.18_65)]"
                    />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4 italic">
                  "{review.text}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-[oklch(0.22_0.06_255)] text-sm">
                      {review.name}
                    </p>
                    <p className="text-gray-600 text-xs">{review.location}</p>
                  </div>
                  <Badge className="bg-[oklch(0.22_0.06_255)/10] text-[oklch(0.22_0.06_255)] text-xs">
                    {review.service}
                  </Badge>
                </div>
              </div>
            ))}
          </div>

          {/* Google Reviews CTA */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://share.google/QKJauQ4chI5M40jjs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white border border-gray-200 shadow-md rounded-xl px-6 py-3 hover:shadow-lg transition-shadow"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="text-left">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(s => <span key={s} className="text-yellow-400 text-sm">★</span>)}
                  <span className="font-bold text-gray-800 text-sm ml-1">4.8</span>
                </div>
                <p className="text-xs text-gray-600">77 Google Reviews — See them all</p>
              </div>
            </a>
            <a
              href="https://share.google/QKJauQ4chI5M40jjs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[oklch(0.72_0.18_65)] text-black font-bold rounded-xl px-6 py-3 hover:opacity-90 transition-opacity text-sm"
            >
              ✍️ Leave a Review
            </a>
          </div>
        </div>
      </section>

      {/* ─── VERIFIED & TRUSTED SECTION ─── */}
      <section className="py-12 bg-white border-y border-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-widest mb-2">Verified & Trusted Across London</p>
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Find Us On These Trusted Platforms</h2>
            <p className="text-gray-600 text-sm mt-2">Our business is verified, reviewed, and listed across the UK's most trusted directories</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Yell */}
            <a href="https://www.yell.com/biz/pest-control-raf-serv-ltd-ruislip-10226416/" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#E31837] flex items-center justify-center">
                <span className="text-white font-black text-lg">Y</span>
              </div>
              <span className="text-xs font-semibold text-gray-700 group-hover:text-amber-600">Yell.com</span>
              <span className="text-xs text-gray-600">Listed ✓</span>
            </a>
            {/* Checkatrade */}
            <a href="https://www.checkatrade.com/trades/pestcontrolrafservltd" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#00A651] flex items-center justify-center">
                <span className="text-white font-black text-sm">✓</span>
              </div>
              <span className="text-xs font-semibold text-gray-700 group-hover:text-amber-600">Checkatrade</span>
              <span className="text-xs text-gray-600">Verified ✓</span>
            </a>
            {/* Trustpilot */}
            <a href="https://uk.trustpilot.com/review/pest-control-raf-serv-ltd.com" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#00B67A] flex items-center justify-center">
                <span className="text-white font-black text-sm">★</span>
              </div>
              <span className="text-xs font-semibold text-gray-700 group-hover:text-amber-600">Trustpilot</span>
              <span className="text-xs text-gray-600">3.5 ★ Reviews</span>
            </a>
            {/* MyJobQuote */}
            <a href="https://www.myjobquote.co.uk/t/pest-control-raf-serv-ltd-1" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#1E3A5F] flex items-center justify-center">
                <span className="text-white font-black text-xs">MJQ</span>
              </div>
              <span className="text-xs font-semibold text-gray-700 group-hover:text-amber-600">MyJobQuote</span>
              <span className="text-xs text-gray-600">Listed ✓</span>
            </a>
            {/* Google */}
            <a href="https://maps.app.goo.gl/ze8Tmeyyw7F5M7ee9" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              </div>
              <span className="text-xs font-semibold text-gray-700 group-hover:text-amber-600">Google</span>
              <span className="text-xs text-gray-600">4.8 ★ (79)</span>
            </a>
            {/* Facebook */}
            <a href="https://www.facebook.com/61568586220299" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-amber-400 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center">
                <span className="text-white font-black text-lg">f</span>
              </div>
              <span className="text-xs font-semibold text-gray-700 group-hover:text-amber-600">Facebook</span>
              <span className="text-xs text-gray-600">Follow Us ✓</span>
            </a>
          </div>
          {/* Trust message */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-600">Fully licensed · BPCA certified · Fully insured · Google Guaranteed · 24/7 emergency service across all London postcodes</p>
          </div>
        </div>
      </section>

      {/* ─── COMMERCIAL SECTION ─── */}
      <section className="py-16 bg-[oklch(0.22_0.06_255)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[oklch(0.72_0.18_65)] text-black font-bold px-4 py-1.5 mb-5">
                Commercial Services
              </Badge>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Protecting London Businesses Since Day One
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                From restaurants and hotels to offices and warehouses — we
                provide tailored commercial pest control contracts that keep your
                business compliant and pest-free.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Essential plan: £75/visit · monthly visits (12/year) · 12-month contract",
                  "Regular scheduled visits with detailed reports",
                  "Compliance documentation for health inspections",
                  "Discreet service to protect your reputation",
                  "Emergency callout included in contracts",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-white/80 text-sm"
                  >
                    <CheckCircle className="w-5 h-5 text-[oklch(0.72_0.18_65)] shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                asChild
                size="lg"
                className="bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold px-8"
              >
                <Link href="/commercial">
                  Get a Commercial Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "🏨", label: "Hotels & B&Bs" },
                { icon: "🍽️", label: "Restaurants & Cafes" },
                { icon: "🏢", label: "Offices & Retail" },
                { icon: "🏭", label: "Warehouses & Factories" },
                { icon: "🏫", label: "Schools & Nurseries" },
                { icon: "🏥", label: "Healthcare Facilities" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="bg-white/10 rounded-xl p-5 text-center border border-white/20 hover:bg-white/15 transition-colors"
                >
                  <div className="text-3xl mb-2">{item.icon}</div>
                  <p className="text-white text-sm font-medium">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── INTERACTIVE TOOLS SECTION ─── */}
      <section className="py-16 bg-[oklch(0.22_0.06_255)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-[oklch(0.72_0.18_65)] text-black font-bold px-4 py-1.5 mb-4">
              Free Tools & Offers
            </Badge>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              Helpful Tools for London Customers
            </h2>
            <p className="text-white/70 max-w-xl mx-auto">
              Use our free tools to identify pests, get price estimates, win discounts, and get expert advice — all free, no obligation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { href: "/spin", icon: "🎰", title: "Spin & Win 10% Off", desc: "Spin our lucky wheel for a chance to win 10% off your next treatment, a free quote, or priority booking.", cta: "Spin Now", highlight: true },
              { href: "/free-advice", icon: "💬", title: "Free Pest Advice AI", desc: "Ask our AI pest advisor anything — DIY tips, identification help, or when to call a professional. Available 24/7.", cta: "Get Free Advice", highlight: false },
              { href: "/identify", icon: "🔍", title: "Identify My Pest", desc: "Upload a photo or describe what you've seen and our AI will identify the pest and recommend treatment.", cta: "Identify Now", highlight: false },
              { href: "/calculator", icon: "💰", title: "Cost Calculator", desc: "Get an instant price estimate for pest control in London. Select your pest, property size, and severity.", cta: "Calculate Price", highlight: false },
              { href: "/pest-calendar", icon: "📅", title: "Pest Season Calendar", desc: "Find out which pests are most active this month in London and how to prevent them.", cta: "View Calendar", highlight: false },
              { href: "/blog", icon: "📖", title: "Pest Control Guides", desc: "Expert guides on how to get rid of rats, mice, cockroaches, bed bugs and more in London homes.", cta: "Read Guides", highlight: false },
            ].map(tool => (
              <Link key={tool.href} href={tool.href}>
                <div className={`rounded-2xl p-6 h-full flex flex-col gap-3 cursor-pointer transition-all hover:scale-105 hover:shadow-xl ${
                  tool.highlight
                    ? "bg-[oklch(0.72_0.18_65)] text-black"
                    : "bg-white/10 text-white hover:bg-white/20"
                }`}>
                  <span className="text-4xl">{tool.icon}</span>
                  <h3 className="font-extrabold text-lg">{tool.title}</h3>
                  <p className={`text-sm flex-1 ${tool.highlight ? "text-black/70" : "text-white/70"}`}>{tool.desc}</p>
                  <span className={`text-sm font-bold flex items-center gap-1 ${tool.highlight ? "text-black" : "text-[oklch(0.72_0.18_65)]"}`}>
                    {tool.cta} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ SECTION ─── */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[oklch(0.22_0.06_255)] mb-3">
              Common Questions About Pest Control in London
            </h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "How quickly can you respond to a pest emergency in London?", a: "We offer same-day emergency pest control across all London postcodes. Call 07586 072087 and we will aim to have a certified technician with you within hours." },
              { q: "Do you charge a call-out fee for pest control in London?", a: "No. We do not charge a call-out fee. You only pay for the treatment carried out. We also provide free quotes before any work begins." },
              { q: "Are your pest control treatments safe for children and pets?", a: "Yes. All treatments are carried out using BPCA-approved, eco-friendly products that are safe for families and pets when used as directed by our certified technicians." },
              { q: "Which pests do you treat in London?", a: "We treat all common London pests including rats, mice, cockroaches, bed bugs, wasps, fleas, ants, squirrels, pigeons, foxes, moths, silverfish, carpet beetles, and more." },
              { q: "How much does pest control cost in London?", a: "Prices start from £70 for wasp nest removal, £95 for mice control, £120 for rat control, and £180 for bed bug treatment. Call 07586 072087 for a free quote tailored to your situation." },
              { q: "Do you provide pest control for landlords and letting agents?", a: "Yes. We work with landlords, letting agents, and property managers across London. We provide full documentation and certificates suitable for tenancy disputes and EHO inspections." },
            ].map((faq, i) => (
              <details key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-[oklch(0.22_0.06_255)] list-none">
                  <span>{faq.q}</span>
                  <span className="ml-4 shrink-0 text-[oklch(0.72_0.18_65)] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link href="/faq" className="inline-flex items-center gap-2 text-[oklch(0.72_0.18_65)] hover:text-[oklch(0.62_0.18_65)] font-semibold text-sm transition-colors">
              View all 250+ pest control questions and answers →
            </Link>
            <span className="mx-4 text-gray-300">|</span>
            <Link href="/pest-control-near-me" className="inline-flex items-center gap-2 text-[oklch(0.72_0.18_65)] hover:text-[oklch(0.62_0.18_65)] font-semibold text-sm transition-colors">
              Pest control near me in London →
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-[oklch(0.22_0.06_255)] mb-4">
            Need a Pest Exterminator in London? Call Now.
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto mb-8">
            Book online in minutes or call 07586 072087 for immediate assistance. No call-out charge. Free quotes. Same-day emergency pest control across all London postcodes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-[oklch(0.22_0.06_255)] hover:bg-[oklch(0.16_0.05_255)] text-white font-bold px-10 py-4 h-auto rounded-xl"
            >
              <Link href="/book">
                <Calendar className="w-5 h-5 mr-2" />
                Book Online Now
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-[oklch(0.22_0.06_255)] text-[oklch(0.22_0.06_255)] hover:bg-[oklch(0.22_0.06_255)] hover:text-white font-bold px-10 py-4 h-auto rounded-xl transition-all"
            >
              <Link href="/quote">
                <MessageSquare className="w-5 h-5 mr-2" />
                Get a Free Quote
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
